import { useEffect, useRef, useState } from "react";
import type { MarketplaceActivityFilter } from "../../../types/marketplaceAccount";

type Props = {
  activity: MarketplaceActivityFilter;
  selectedMarketplaceTypes: string[];
  marketplaceTypes: string[];
  onActivityChange: (activity: MarketplaceActivityFilter) => void;
  onMarketplaceTypesChange: (marketplaceTypes: string[]) => void;
};

const tabs: Array<{ value: MarketplaceActivityFilter; label: string }> = [
  { value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
];

export function MarketplaceAccountsToolbar(props: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [temporarySelection, setTemporarySelection] = useState(props.selectedMarketplaceTypes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setTemporarySelection(props.selectedMarketplaceTypes);
  }, [isOpen, props.selectedMarketplaceTypes]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const toggleMarketplace = (type: string) => {
    setTemporarySelection((current) => current.includes(type)
      ? current.filter((item) => item !== type)
      : [...current, type]);
  };

  const selectedCount = props.selectedMarketplaceTypes.length;
  const selectionLabel = selectedCount === 0 ? "All marketplaces" : selectedCount === 1 ? props.selectedMarketplaceTypes[0] : `${selectedCount} marketplaces selected`;

  return (
    <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-3 tw-border-b tw-border-slate-200 tw-px-4 tw-py-3 dark:tw-border-slate-700 sm:tw-px-6">
      <div className="tw-relative tw-self-start" ref={dropdownRef}>
        <button type="button" className="tw-flex tw-h-9 tw-max-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 dark:tw-border-slate-600 dark:tw-bg-slate-800 dark:tw-text-slate-200 dark:hover:tw-bg-slate-700" onClick={() => setIsOpen((open) => !open)} aria-expanded={isOpen} aria-haspopup="listbox" aria-label={`Marketplace filter, ${selectionLabel}`}>
          <span className="material-symbols-outlined tw-text-base">store</span>
          <span className="tw-max-w-56 tw-truncate">{selectionLabel}</span>
          {selectedCount > 1 && <span className="tw-inline-flex tw-h-5 tw-min-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-1 tw-text-[10px] tw-font-bold tw-text-white">{selectedCount}</span>}
          <span className={`material-symbols-outlined tw-text-base tw-transition-transform ${isOpen ? "tw-rotate-180" : ""}`}>expand_more</span>
        </button>
        {isOpen && (
          <div className="tw-absolute tw-left-0 tw-top-full tw-z-30 tw-mt-2 tw-flex tw-w-64 tw-max-w-[calc(100vw-2rem)] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-shadow-lg dark:tw-border-slate-700 dark:tw-bg-slate-800" role="listbox" aria-label="Marketplace options">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-100 tw-p-3 dark:tw-border-slate-700"><span className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">Select marketplaces</span><button type="button" className="tw-text-xs tw-font-medium tw-text-primary disabled:tw-cursor-not-allowed disabled:tw-opacity-40" disabled={temporarySelection.length === 0} onClick={() => setTemporarySelection([])}>Clear all</button></div>
            <div className="tw-max-h-64 tw-overflow-y-auto tw-p-2">
              <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-p-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 dark:tw-text-slate-200 dark:hover:tw-bg-slate-700"><input type="checkbox" checked={temporarySelection.length === 0} onChange={() => setTemporarySelection([])} className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-primary focus:tw-ring-primary" />All marketplaces</label>
              {props.marketplaceTypes.map((type) => <label key={type} className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-p-2 tw-text-sm tw-text-slate-600 hover:tw-bg-slate-50 dark:tw-text-slate-300 dark:hover:tw-bg-slate-700"><input type="checkbox" checked={temporarySelection.includes(type)} onChange={() => toggleMarketplace(type)} className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-primary focus:tw-ring-primary" />{type}</label>)}
            </div>
            <div className="tw-flex tw-justify-end tw-border-t tw-border-slate-100 tw-p-2 dark:tw-border-slate-700"><button type="button" className="tw-rounded-md tw-bg-primary tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-primary/90" onClick={() => { props.onMarketplaceTypesChange(temporarySelection); setIsOpen(false); }}>Apply</button></div>
          </div>
        )}
      </div>
      <nav className="tw-flex tw-gap-5 tw-overflow-x-auto" aria-label="Marketplace account status">
        {tabs.map((tab) => <button key={tab.value} type="button" onClick={() => props.onActivityChange(tab.value)} className={`tw-border-b-2 tw-pb-2 tw-text-sm tw-font-semibold ${props.activity === tab.value ? "tw-border-primary tw-text-primary" : "tw-border-transparent tw-text-slate-500 hover:tw-text-slate-900 dark:hover:tw-text-white"}`}>{tab.label}</button>)}
      </nav>
    </div>
  );
}
